import { getApiUrl } from "@ihs-widget/config/api";
import { getLocaleFromPath } from "@ihs-widget/api/commonAPI";
import type { ApiResponse, TeamColors, TeamNames } from "@ihs-widget/api/interfaces";
import { processMatchClockData } from "@ihs-widget/api/commonAPI";

export const getOverviewDataByIdFromMartechBackend = async (eventId: string | null): Promise<ApiResponse<any>> => {
    try {
        const apiUrl = getApiUrl(`/api/overview/${eventId}`);
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return { data, error: null };
    } catch (error) {
        console.error(`Error fetching overview data:`, error);
        return { 
            data: null, 
            error: error instanceof Error ? error : new Error('Unknown error') 
        };
    }
}

export const getStatsConsoleData = async (eventId: string | null) : Promise<ApiResponse<any>> => {
    try {
        const apiUrl = getApiUrl(`/api/stats-console/${eventId}`);
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        return { data: data?.statistics, error: null };
    } catch (error) {
        console.error(`Error fetching players-tab data:`, error);
        return { 
            data: null, 
            error: error instanceof Error ? error : new Error('Unknown error') 
        };
    }
}

export const getStatsTabData = async (eventId: string | null): Promise<ApiResponse<any>> => {
    try {
        const apiUrl = getApiUrl(`/api/stats-tab/${eventId}`);
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const rawData = await response.json();
        
        const formattedData = {
            default: {
                match_statistics: rawData.match_statistics
            },
            match_statistics: rawData.match_statistics,
            [Symbol.toStringTag]: "Module"
        };
        
        return {
            data: formattedData,
            error: null
        }
    } catch (error) {
        console.error('Error fetching stats tab data:', error);
        return {
            data: null,
            error: error instanceof Error ? error : new Error('Unknown error')
        };
    }
}

export const getPlayersTabData = async (eventId: string | null): Promise<ApiResponse<any>> => {
    try {
        const apiUrl = getApiUrl(`/api/players-tab/${eventId}`);
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return { data, error: null };
    } catch (error) {
        console.error(`Error fetching players-tab data:`, error);
        return { 
            data: null, 
            error: error instanceof Error ? error : new Error('Unknown error') 
        };
    }
}

export const getH2HData = async (eventId: string | null): Promise<ApiResponse<any>> => {
    try {
        const locale = getLocaleFromPath();
        const endpoint = `/api/h2h/${eventId}?locale=${locale}`;
        
        const apiUrl = getApiUrl(endpoint);
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return { data, error: null };
    } catch (error) {
        console.error(`Error fetching h2h data:`, error);
        return { 
            data: null, 
            error: error instanceof Error ? error : new Error('Unknown error') 
        };
    }
}


export const getLineupData = async (eventId: string | null): Promise<ApiResponse<unknown>> => {
    try {
        const apiUrl = getApiUrl(`/api/lineup/${eventId}`);
        const response = await fetch(apiUrl);

        if (response.ok) {
            const data = await response.json();
            return { data, error: null };
        }

        return { data: null, error: new Error(`HTTP error! status: ${response.status}`) };
    } catch (error) {
        console.error('Error fetching lineup data:', error);
        return {
            data: null,
            error: error instanceof Error ? error : new Error('Unknown error')
        };
    }
}


export const getTeamColorsAPI = async (eventId: string | null): Promise<ApiResponse<TeamColors>> => {
    try {
        if (!eventId) {
            throw new Error('Event ID is required');
        }

        const apiUrl = getApiUrl(`/api/team-colors/${eventId}`);
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Check if home and away colors are available in new structure
        if (data?.home?.color && data?.away?.color) {
            return { 
                data: { 
                    homeColor: data.home.color, 
                    awayColor: data.away.color 
                }, 
                error: null 
            };
        } else {
            // Return default colors if API colors are not available
            return { 
                data: { 
                    homeColor: '#147B45', 
                    awayColor: '#333333' 
                }, 
                error: null 
            };
        }
        
    } catch (error) {
        console.error('Error fetching team colors from API:', error);
        // Return default colors on error
        return { 
            data: { 
                homeColor: '#147B45', 
                awayColor: '#333333' 
            }, 
            error: error instanceof Error ? error : new Error('Unknown error') 
        };
    }
};

export const getTeamNames = async (eventId: string | null): Promise<ApiResponse<TeamNames>> => {
    try {
        const locale = getLocaleFromPath();
        const apiUrl = getApiUrl(`/api/team-names/${eventId}?locale=${locale}`);
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return { data, error: null };
    } catch (error) {
        console.error(`Error fetching team names:`, error);
        return { 
            data: null, 
            error: error instanceof Error ? error : new Error('Unknown error') 
        };
    }
}

export const getOverviewDataFromOurBackend = async (eventId: string | null): Promise<ApiResponse<any>> => {
    try {
        const overviewResponse = await getOverviewDataByIdFromMartechBackend(eventId);
        
        if (overviewResponse.error) {
            return overviewResponse;
        }
        
        if (overviewResponse.data?.match_clock) {
            const processedMatchClock = processMatchClockData(overviewResponse.data.match_clock);
            overviewResponse.data.match_clock = processedMatchClock;
        }
        
        return overviewResponse;
        
    } catch (error) {
        console.error('Error fetching overview data from backend:', error);
        return { 
            data: null, 
            error: error instanceof Error ? error : new Error('Unknown error') 
        };
    }
};
